<template>
  <div class="editor-container" :class="{ fullscreen: isFullscreen }">
    <span v-if="label" :style="labelStyle">{{ label }}</span>
    <div :class="readOnly ? 'disabled' : ''">
      <QuillEditor
          v-model:content="editorContent"
          content-type="html"
          :options="editorOptions"
          :style="inputStyle"
          @text-change="onTextChange"/>
    </div>
  </div>
</template>

<script setup>
import { Quill, QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { defineEmits, defineProps, onMounted, ref, toRefs, watch } from 'vue';

const props = defineProps({
  modelValue: String, // Two-way binding support
  inputStyle: Object,
  label: {
    type: [ String, Boolean ],
    default: false,
  },
  labelStyle: Object,
  readOnly: {
    type: Boolean,
    default: false,
  },
  classForm: String,
});

const editorContent = ref(props.modelValue);
const emit          = defineEmits([ 'update:modelValue' ]);
const readOnly      = toRefs(props)[ 'readOnly' ];

watch(() => props.modelValue, (newValue) => {
  editorContent.value = newValue;
});

const onTextChange = () => {
  emit('update:modelValue', editorContent.value);
};


const toolbarOptions = [
  ['bold', 'italic', 'underline', 'strike'],
  [{ header: 1 }, { header: 2 }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ 'size': ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '32px', '48px', '64px'] }],
  [{ 'align': [] }],
  //['link', 'image'],
  //[{ fullscreen: true }] // Fullscreen Button at END of Toolbar
];

const Size = Quill.import('formats/size');
Size.whitelist = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '32px', '48px', '64px'];

// Define the editor options
const editorOptions = {
  modules: {
    toolbar: {
      container: toolbarOptions,
      handlers: {
        fullscreen() {
          toggleFullscreen();
        },
      },
    }
  },
  theme: 'snow',
};

const isFullscreen = ref(false);

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value;

  const editorContainer = document.querySelector('.editor-container');
  if (isFullscreen.value) {
    editorContainer.classList.add('fullscreen');
  } else {
    editorContainer.classList.remove('fullscreen');
  }
};

onMounted(() => {
  const toolbar = document.querySelector('.ql-toolbar');
  const customGroup = document.createElement('span');
  customGroup.className = 'ql-formats'; // Quill's grouping class
  const button = document.createElement('button');
  button.className = 'ql-fullscreen';
  button.innerHTML = '<i class="fas fa-expand"></i>'; // Font Awesome Icon for fullscreen
  customGroup.appendChild(button);

  // Insert at the END of the toolbar
  // toolbar.appendChild(customGroup);

  // Register click event on button and prevent the default action
  button.addEventListener('click', (event) => {
    event.preventDefault(); // Prevent the default button click behavior
    toggleFullscreen(); // Call the fullscreen toggle logic
  });
});

</script>

<style scoped>
.disabled {
  background: #e9ecef;
}

.fullscreen .ql-editor {
  height: 100% !important; /* Ensure the editor takes up the full height */
  max-height: none !important;
}

.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #fff;
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

.fullscreen .ql-toolbar {
  position: relative;
  z-index: 999; /* Keep toolbar on top in fullscreen */
}

button.ql-fullscreen {
  background: #007bff;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
  width: 100%;
}

button.ql-fullscreen i {
  font-size: 20px; /* Adjust icon size */
}

button.ql-fullscreen:hover {
  background: #007bff;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
  width: 100%;
}

</style>
